import {
  UseInfiniteQueryOptions,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import { client } from '@/services/api/axios.ts';
import { TNotification } from '@/features/notifications/schemas/notifications.schemas.ts';
import { NotificationFilter } from '@/services/store/use-notifications-store.ts';

// Example found on: https://github.com/TanStack/query/discussions/4941 => v5 syntax playground
interface NotificationParams {
  limit?: number;
  notificationFilter: NotificationFilter;
}

interface PageParam {
  nextCursor?: string;
  currentPage: number;
}

export interface NotificationResponse {
  length: number;
  body: TNotification[];
  nextPage?: number | null;
  nextCursor?: string | null;
  currentPage?: number;
}

async function fetchNotifications(
  pageParam: PageParam,
  { limit = 10, notificationFilter = 'all' }: NotificationParams
): Promise<NotificationResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    currentPage: String(pageParam.currentPage),
    ...(notificationFilter !== 'all' && { status: notificationFilter }),
    ...(pageParam.nextCursor && { nextCursor: pageParam.nextCursor }),
  });

  try {
    const { data } = await client.get<NotificationResponse>(`/notifications?${params}`);
    return data;
  } catch (error) {
    const message = axios.isAxiosError(error)
      ? error.response?.data?.message
      : error instanceof Error
        ? error.message
        : 'An unknown error occurred';
    throw new Error(message);
  }
}

export function useGetNotificationsQuery(
  options: NotificationParams = { limit: 10, notificationFilter: 'all' },
  config?: UseInfiniteQueryOptions<
    NotificationResponse,
    Error,
    TNotification[],
    string[],
    PageParam
  >
) {
  const { notificationFilter } = options;
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    ...config,
    queryKey: ['notifications', notificationFilter],
    queryFn: ({ pageParam }) => fetchNotifications(pageParam, options),
    initialPageParam: { nextCursor: undefined, currentPage: 0 },
    getNextPageParam: (lastPage) =>
      lastPage?.nextPage && lastPage?.nextCursor
        ? { nextCursor: lastPage.nextCursor, currentPage: lastPage.nextPage }
        : undefined,
    select: (data) => ({
      ...data,
      pages: data.pages.flatMap((page) => page.body),
      isLastPage: !data.pages.at(-1)?.nextCursor,
    }),
    staleTime: 60_000,
  });

  const prefetchNotifications = (filter: NotificationFilter) => {
    queryClient.prefetchInfiniteQuery({
      queryKey: ['notifications', filter],
      queryFn: ({ pageParam }) =>
        fetchNotifications(pageParam, { notificationFilter: filter }),
      initialPageParam: { nextCursor: undefined, currentPage: 0 },
      staleTime: 60_000,
    });
  };

  return {
    ...query,
    prefetchNotifications,
  };
}
